import { useReducer, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import type { Message, ChatWidgetProps, SessionConfig, RetryPolicy, MessageResult } from '../types';
import { STORAGE_KEY, readScopedItem, type StorageScope } from '../constants/storage';
import { chatReducer, initialChatState } from './chatReducer';
import { resolveEndpoint, getNextMessageId } from '../utils/chatHelpers';
import { createStreamingMessageFn } from './streamingMessage';
import { withRequestTimeout } from '../utils/fetchWithTimeout';
import { isBrowserOffline } from './useOnlineStatus';
import { notifyHost } from '../utils/notifyHost';
import type { WidgetStrings } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../utils/i18n';
import {
  buildAttachmentPayload,
  attachmentMetadata,
  buildChatHeaders,
  buildChatPayload,
  postChatJson,
} from './chatRequest';
import type { AttachmentPayload, ChatPayloadArgs, ComposerAttachment } from './chatRequest';
import type { ThreadFetchResult } from './useThread';
import {
  applyChatResponseEffects,
  applySendFailureEffects,
  isNetworkSendError,
  buildChatAnalyticsEnvelope,
  parseChatReply,
  streamedReplyAnalytics,
} from './chatResponseEffects';

interface UseChatOptions {
  enableStreaming: boolean;
  enableToolVisualization: boolean;
  enableRichMessages: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  streamEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  messageCreateEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  provider?: string;
  subject: string;
  department: string;
  channel: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  threadId: string | null;
  threadBase: string | null;
  companyName: string;
  saveHistory: boolean;
  loadHistory?: boolean;
  historyStorage?: 'local' | 'session';
  /** Namespaces the history key per org/agent (legacy global key when absent). */
  storageScope?: StorageScope;
  buildMessagePayload?: ChatWidgetProps['buildMessagePayload'];
  parseResponse?: ChatWidgetProps['parseResponse'];
  onSend?: ChatWidgetProps['onSend'];
  onReceive?: ChatWidgetProps['onReceive'];
  onError?: ChatWidgetProps['onError'];
  onMessageCreate?: ChatWidgetProps['onMessageCreate'];
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  trackOutcome?: (outcome: unknown) => void;
  withAuthHeaders: (headers?: Record<string, string>) => Record<string, string>;
  fetchThread: () => Promise<void>;
  fetchThreadResult?: () => Promise<ThreadFetchResult>;
  createThread: (args?: {
    subject?: string;
    message?: string;
    meta?: Record<string, unknown>;
    ch?: string;
    signal?: AbortSignal;
  }) => Promise<{ threadId: string; raw: unknown }>;
  threadCreateEndpoint: string | null | undefined;
  minimized: boolean;
  setThreadId: (id: string | null) => void;
  /**
   * Capability token handed back with a chat response (JSON `threadToken` or the
   * leading SSE `thread` event). Wire to `useThread().onThreadIssued`; when omitted
   * the JSON path persists the token itself.
   */
  onThreadIssued?: (issued: { threadId?: string | null; threadToken?: string | null }) => void;
  sessionConfig?: SessionConfig;
  retryPolicy?: RetryPolicy;
  /** Resolved widget string table; user-facing send-failure copy comes from here. */
  strings?: WidgetStrings;
}

type SendResult = {
  ok: boolean;
  aborted?: boolean;
};

interface CreateMessageArgs extends ChatPayloadArgs {
  sender?: 'user' | 'ai';
  /** Id for the local echo bubble so the caller can mark it failed later. */
  localMessageId?: string;
}

/** English default; the localized table (`strings.sendFailedGeneric`) wins at runtime. */
export const SEND_FAILED_GENERIC_MESSAGE = DEFAULT_WIDGET_STRINGS.sendFailedGeneric;

export function useChat(options: UseChatOptions) {
  const {
    enableStreaming,
    enableToolVisualization,
    enableRichMessages,
    enableSourceCitations,
    enableThinking,
    streamEndpoint,
    messageEndpoint,
    messageCreateEndpoint,
    apiBaseUrl,
    subject,
    provider,
    department,
    channel,
    customFields,
    metaExtras,
    threadId,
    threadBase,
    companyName,
    saveHistory,
    loadHistory = true,
    historyStorage = 'local',
    storageScope,
    buildMessagePayload,
    parseResponse,
    onSend,
    onReceive,
    onError,
    onMessageCreate,
    trackAnalytics,
    trackOutcome,
    withAuthHeaders,
    fetchThread,
    fetchThreadResult,
    createThread,
    threadCreateEndpoint,
    setThreadId,
    sessionConfig,
    retryPolicy,
    onThreadIssued,
    strings = DEFAULT_WIDGET_STRINGS,
  } = options;

  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [historyLoaded, setHistoryLoaded] = useState(!(loadHistory && saveHistory));
  const abortControllerRef = useRef<AbortController | null>(null);
  const preparationControllerRef = useRef<AbortController | null>(null);
  // Id of the request (streaming or JSON) allowed to touch state; older requests bail out.
  const activeRequestIdRef = useRef<string | null>(null);
  // Monotonic send counter so only the most recent handleSend settles loading/isTyping.
  const sendSeqRef = useRef(0);
  const retryAttachmentsRef = useRef(new Map<string, ComposerAttachment[]>());
  const retryInFlightRef = useRef(new Map<string, Promise<SendResult>>());
  const mountedRef = useRef(true);
  const stateRef = useRef(state);

  const abortActiveStream = useCallback(() => {
    preparationControllerRef.current?.abort();
    abortControllerRef.current?.abort();
  }, []);

  /** Conversation changes discard old partial replies; Stop keeps the current partial reply. */
  const discardActiveSend = useCallback(() => {
    activeRequestIdRef.current = null;
    sendSeqRef.current++;
    abortActiveStream();
    retryAttachmentsRef.current.clear();
    retryInFlightRef.current.clear();
    dispatch({ type: 'CLEAR_STREAMING' });
    dispatch({ type: 'SEND_SUCCESS' });
    dispatch({ type: 'CLEAR_ERROR' });
    dispatch({ type: 'SET_ESCALATED', isEscalated: false });
  }, [abortActiveStream]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Abort in-flight stream and mark unmounted
  useEffect(() => {
    mountedRef.current = true;
    const retryAttachments = retryAttachmentsRef.current;
    const retries = retryInFlightRef.current;
    return () => {
      mountedRef.current = false;
      activeRequestIdRef.current = null;
      retryAttachments.clear();
      retries.clear();
      abortActiveStream();
    };
  }, [abortActiveStream]);

  // Load history from storage on mount
  useEffect(() => {
    if (!loadHistory || !saveHistory) {
      setHistoryLoaded(true);
      return;
    }
    try {
      const store = historyStorage === 'session' ? sessionStorage : localStorage;
      const raw = readScopedItem(store, STORAGE_KEY, storageScope);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) dispatch({ type: 'SET_MESSAGES', messages: parsed });
      }
    } catch {
      // storage may be unavailable (private browsing, storage full, SSR)
    } finally {
      setHistoryLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMessages = useCallback((updater: Message[] | ((prev: Message[]) => Message[])) => {
    const next = typeof updater === 'function' ? updater(stateRef.current.messages) : updater;
    if (next.length === 0) retryAttachmentsRef.current.clear();
    dispatch({ type: 'SET_MESSAGES', messages: next });
  }, []);

  const buildAnalyticsEnvelope = useCallback(
    (extra: Record<string, unknown> = {}) =>
      buildChatAnalyticsEnvelope(
        { sessionConfig, threadId, channel, provider, department, subject },
        extra
      ),
    [channel, department, provider, sessionConfig, subject, threadId]
  );

  const handleJsonResponse = useCallback(
    async (
      data: Record<string, unknown>,
      localMsg: Message,
      atts: AttachmentPayload[],
      requestMeta?: Record<string, unknown>,
      isActive?: () => boolean
    ): Promise<MessageResult> => {
      // Nothing below may run once the hook unmounted or a newer request took over:
      // it would dispatch into a dead reducer, fire callbacks for a stale turn, and
      // navigate/refresh the thread on the caller's behalf.
      if (!mountedRef.current || (isActive && !isActive())) {
        return { request: localMsg, raw: data, aborted: true };
      }
      const parsed = parseChatReply(data, {
        parseResponse,
        enableRichMessages,
        enableSourceCitations,
        enableToolVisualization,
        strings,
      });
      return applyChatResponseEffects(
        { data, parsed, localMsg, attachments: atts, requestMeta },
        {
          threadId,
          setThreadId,
          onThreadIssued,
          sessionConfig,
          trackAnalytics,
          trackOutcome,
          buildAnalyticsEnvelope,
          dispatch,
          onMessageCreate,
          fetchThread,
          isActive: () => mountedRef.current && (!isActive || isActive()),
        }
      );
    },
    [
      strings,
      parseResponse,
      enableRichMessages,
      enableSourceCitations,
      enableToolVisualization,
      threadId,
      setThreadId,
      onMessageCreate,
      trackAnalytics,
      trackOutcome,
      fetchThread,
      buildAnalyticsEnvelope,
      sessionConfig,
      onThreadIssued,
    ]
  );

  // Shared payload builder for streaming and non-streaming
  const buildPayload = useCallback(
    (args: ChatPayloadArgs) =>
      buildChatPayload(args, {
        subject,
        department,
        customFields,
        metaExtras,
        provider,
        channel,
        threadId,
        sessionConfig,
        messages: state.messages,
      }),
    [
      subject,
      department,
      customFields,
      metaExtras,
      provider,
      channel,
      threadId,
      sessionConfig,
      state.messages,
    ]
  );

  const createMessage = useCallback(
    async ({
      text,
      attachments: atts = [],
      subject: subj,
      tid,
      sender = 'user',
      meta,
      localMessageId,
    }: CreateMessageArgs): Promise<MessageResult> => {
      if (!mountedRef.current) throw new DOMException('Request aborted', 'AbortError');
      // Direct API sends can also supersede a composer send that is still preparing files.
      preparationControllerRef.current?.abort();
      preparationControllerRef.current = null;
      if (enableStreaming && (streamEndpoint || messageEndpoint)) {
        // Streaming path lives in streamingMessage.ts; it shares the payload
        // builder, JSON fallback handler and abort/active-request refs.
        return createStreamingMessageFn({
          text,
          attachments: atts,
          subject: subj,
          tid,
          sender,
          meta,
          localMessageId,
          onThreadIssued,
          streamEndpoint,
          messageCreateEndpoint,
          messageEndpoint,
          apiBaseUrl,
          threadId,
          threadBase,
          dispatch,
          mountedRef,
          abortControllerRef,
          activeRequestIdRef,
          withAuthHeaders,
          buildPayload,
          buildMessagePayload,
          handleJsonResponse,
          onMessageCreate,
          trackAnalytics,
          trackOutcome,
          buildAnalyticsEnvelope,
          enableToolVisualization,
          enableSourceCitations,
          enableThinking,
          enableRichMessages,
          retryPolicy,
          sessionConfig,
        });
      }

      const endpoint = resolveEndpoint({
        messageCreate: messageCreateEndpoint,
        message: messageEndpoint,
        threadBase,
        tid: tid || threadId,
        apiBaseUrl,
      });
      if (!endpoint) throw new Error('No message endpoint configured');

      const now = new Date();
      const localId = localMessageId ?? getNextMessageId();
      const localMsg: Message = {
        id: localId,
        sender,
        text,
        timestamp: now.toISOString(),
        status: 'sent',
        ...(atts.length ? { attachments: attachmentMetadata(atts, localId) } : {}),
      };
      if (!(meta && typeof meta === 'object' && meta.silent === true)) {
        dispatch({ type: 'ADD_MESSAGE', message: localMsg });
      }

      // Mirror the streaming path: this request becomes the active one, any
      // in-flight predecessor is cancelled, and it owns its own controller so
      // abortActiveStream()/unmount can cancel it.
      // Attempts need distinct ownership even when a retry keeps its bubble's identity.
      const requestId = `${getNextMessageId()}-json`;
      activeRequestIdRef.current = requestId;
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const isActive = () => mountedRef.current && activeRequestIdRef.current === requestId;

      const payload = {
        ...buildPayload({ text, attachments: atts, subject: subj, tid, meta }),
        clientMessageId: meta?.clientMessageId || localMsg.id,
      };
      const bodyPayload =
        typeof buildMessagePayload === 'function'
          ? buildMessagePayload(payload, localMsg)
          : payload;
      const requestStartedAt = Date.now();
      const data = await postChatJson({
        endpoint,
        body: bodyPayload,
        headers: buildChatHeaders(withAuthHeaders, tid || threadId, undefined, sessionConfig),
        signal: controller.signal,
        retryPolicy,
        onRetry: metadata => {
          trackAnalytics(
            'message_retry',
            buildAnalyticsEnvelope({
              threadId: tid || threadId || undefined,
              attempt: metadata.attempt,
              reason: metadata.reason,
              status: metadata.status,
            })
          );
        },
      });
      const result = await handleJsonResponse(
        data,
        localMsg,
        atts,
        {
          ...meta,
          analyticsRequestStartedAt: requestStartedAt,
        },
        isActive
      );
      if (activeRequestIdRef.current === requestId) activeRequestIdRef.current = null;
      return result;
    },
    [
      enableStreaming,
      streamEndpoint,
      messageCreateEndpoint,
      messageEndpoint,
      apiBaseUrl,
      threadId,
      threadBase,
      withAuthHeaders,
      buildMessagePayload,
      buildPayload,
      handleJsonResponse,
      onMessageCreate,
      onThreadIssued,
      trackAnalytics,
      trackOutcome,
      buildAnalyticsEnvelope,
      enableToolVisualization,
      enableSourceCitations,
      enableThinking,
      enableRichMessages,
      retryPolicy,
      sessionConfig,
    ]
  );

  /**
   * Shared send pipeline for the composer (`handleSend`) and Retry
   * (`retryMessage`). Owns the loading/typing lifecycle and turns a failure
   * into a failed user bubble rather than a banner + restored draft.
   */
  const sendMessage = useCallback(
    async (
      text: string,
      attachments: ComposerAttachment[],
      meta?: Record<string, unknown>,
      retryMessageId?: string
    ): Promise<SendResult> => {
      meta = {
        ...meta,
        clientMessageId:
          meta?.clientMessageId ||
          (typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      };
      if (attachments.length)
        retryAttachmentsRef.current.set(String(meta.clientMessageId), attachments);
      const sendSeq = ++sendSeqRef.current;
      activeRequestIdRef.current = null;
      abortActiveStream();
      const preparationController = new AbortController();
      preparationControllerRef.current = preparationController;
      dispatch({ type: 'CLEAR_STREAMING' });
      dispatch({ type: 'SEND_START' });
      // Only the newest send may settle loading/isTyping; a superseded send that
      // resolves late (usually as aborted) must not turn the indicators off while
      // the replacement request is still streaming.
      const isLatestSend = () => mountedRef.current && sendSeqRef.current === sendSeq;
      // Chosen up front so a failure anywhere in the pipeline (thread creation,
      // attachment encoding, the request itself) can mark the same bubble failed.
      const localMessageId = retryMessageId ?? getNextMessageId();
      const attachmentKey = String(meta.clientMessageId);
      const releaseAttachments = () => {
        if (retryAttachmentsRef.current.get(attachmentKey) === attachments)
          retryAttachmentsRef.current.delete(attachmentKey);
      };

      try {
        // Don't fire a request that cannot succeed: queue the bubble for reconnect.
        if (isBrowserOffline()) throw new Error('Failed to fetch (offline)');
        const subj = subject || (text ? text.slice(0, 80) : `New message from ${companyName}`);
        const { ensuredThreadId, atts } = await withRequestTimeout(
          async signal => {
            let ensuredThreadId = threadId;
            if (!ensuredThreadId && threadCreateEndpoint) {
              const created = await createThread({ subject: subj, ch: channel, signal });
              ensuredThreadId = created?.threadId || threadId;
            }
            if (signal.aborted) throw signal.reason;
            const atts = attachments.length
              ? await buildAttachmentPayload(attachments, signal)
              : [];
            return { ensuredThreadId, atts };
          },
          { signal: preparationController.signal, timeoutMs: 30_000 }
        );
        if (!isLatestSend() || preparationController.signal.aborted) {
          if (isLatestSend()) dispatch({ type: 'SEND_SUCCESS' });
          return { ok: false, aborted: true };
        }
        if (preparationControllerRef.current === preparationController)
          preparationControllerRef.current = null;
        const requestStartedAt = Date.now();
        const result = await createMessage({
          text,
          attachments: atts,
          subject: subj,
          tid: ensuredThreadId || undefined,
          meta,
          localMessageId,
        });
        releaseAttachments();
        if (!isLatestSend()) return { ok: false, aborted: true };
        if (!result?.aborted) {
          notifyHost(onSend, result.request);
          if (result?.response && isLatestSend()) {
            notifyHost(onReceive, result.response);
            if (result.streamed && isLatestSend()) {
              trackAnalytics(
                'message_received',
                buildAnalyticsEnvelope(
                  streamedReplyAnalytics(result.response, {
                    text,
                    threadId: ensuredThreadId || threadId || undefined,
                    elapsedMs: Date.now() - requestStartedAt,
                  })
                )
              );
            }
          }
        }
        if (!isLatestSend()) return { ok: false, aborted: true };
        if (isLatestSend()) dispatch({ type: 'SEND_SUCCESS' });
        return { ok: !result?.aborted, aborted: result?.aborted };
      } catch (err) {
        if (
          !isLatestSend() ||
          preparationController.signal.aborted ||
          (err instanceof DOMException && err.name === 'AbortError')
        ) {
          // Cancelled on purpose (Stop button, newer send, unmount): not an error.
          releaseAttachments();
          if (isLatestSend()) dispatch({ type: 'SEND_SUCCESS' });
          return { ok: false, aborted: true };
        }
        const errorObj = err instanceof Error ? err : new Error(String(err));
        applySendFailureEffects(
          errorObj,
          {
            text,
            attachmentCount: attachments.length,
            attachments,
            meta,
            localMessageId,
            mounted: mountedRef.current,
            isLatestSend: isLatestSend(),
            offline: isBrowserOffline() && isNetworkSendError(errorObj),
          },
          { strings, trackAnalytics, buildAnalyticsEnvelope, dispatch, onError }
        );
        return { ok: false };
      } finally {
        if (preparationControllerRef.current === preparationController)
          preparationControllerRef.current = null;
      }
    },
    [
      strings,
      threadId,
      subject,
      companyName,
      threadCreateEndpoint,
      createThread,
      channel,
      createMessage,
      onSend,
      onReceive,
      onError,
      trackAnalytics,
      buildAnalyticsEnvelope,
      abortActiveStream,
    ]
  );

  const handleSend = useCallback(
    async (
      attachments: ComposerAttachment[],
      meta?: Record<string, unknown>
    ): Promise<SendResult> => {
      const text = state.input.trim();
      if (!text && attachments.length === 0) return { ok: false };
      dispatch({ type: 'SET_INPUT', input: '' });
      return sendMessage(text, attachments, meta);
    },
    [state.input, sendMessage]
  );

  /** Coalesces retries and refuses to resend an upload after its in-memory files were lost. */
  const retryMessage = useCallback(
    (messageId: string): Promise<SendResult> => {
      const existing = retryInFlightRef.current.get(messageId);
      if (existing) return existing;
      const failed = stateRef.current.messages.find(
        m => m.id === messageId && m.status === 'failed'
      );
      if (!failed) return Promise.resolve({ ok: false });
      const performRetry = async (): Promise<SendResult> => {
        if (failed.meta?.outcomeUnknown) {
          const recoverySequence = sendSeqRef.current;
          const isCurrent = () => mountedRef.current && sendSeqRef.current === recoverySequence;
          let result: ThreadFetchResult;
          try {
            result = fetchThreadResult
              ? await fetchThreadResult()
              : (await fetchThread(), { ok: true });
          } catch (error) {
            result = {
              ok: false,
              error: error instanceof Error ? error : new Error(String(error)),
            };
          }
          if (!isCurrent() || result.reason === 'aborted') return { ok: false, aborted: true };
          dispatch({
            type: 'SET_RECOVERY_ERROR',
            id: messageId,
            error: result.ok ? strings.replyRecoveryPending : strings.replyRecoveryFailed,
          });
          if (result.error) notifyHost(onError, result.error);
          return { ok: false };
        }
        const attachments = retryAttachmentsRef.current.get(String(failed.meta?.clientMessageId));
        if (failed.attachments?.length && attachments?.length !== failed.attachments.length) {
          dispatch({
            type: 'MARK_MESSAGE_FAILED',
            message: failed,
            error: strings.sendFailedAttachmentsMissing,
            meta: { ...failed.meta, retryRequiresAttachments: true },
          });
          return { ok: false };
        }
        // Keep the failed row anchored while thread creation or file preparation is pending.
        // A refresh already preserves unacknowledged failed rows; the local echo will replace
        // this same id in place once the request starts.
        dispatch({ type: 'ADD_MESSAGE', message: { ...failed, retryOnReconnect: false } });
        return sendMessage(failed.text, attachments || [], failed.meta, failed.id);
      };
      // Register before starting: a synchronous failure can invoke a host's onError callback,
      // which may immediately ask to retry this same message again.
      let resolve!: (result: SendResult) => void;
      let reject!: (error: unknown) => void;
      const pending = new Promise<SendResult>((done, fail) => {
        resolve = done;
        reject = fail;
      });
      retryInFlightRef.current.set(messageId, pending);
      void performRetry().then(resolve, reject);
      const release = () => {
        if (retryInFlightRef.current.get(messageId) === pending)
          retryInFlightRef.current.delete(messageId);
      };
      void pending.then(release, release);
      return pending;
    },
    [sendMessage, fetchThread, fetchThreadResult, onError, strings]
  );

  // Memoized so consumers that depend on `[chat]` don't rebuild every render.
  return useMemo(
    () => ({
      state,
      dispatch,
      setMessages,
      createMessage,
      handleSend,
      retryMessage,
      historyLoaded,
      /** Cancels the in-flight request (streaming or JSON). Wire this to a Stop button. */
      abortActiveStream,
      discardActiveSend,
    }),
    [
      state,
      setMessages,
      createMessage,
      handleSend,
      retryMessage,
      historyLoaded,
      abortActiveStream,
      discardActiveSend,
    ]
  );
}
